import React, { useMemo } from "react";
import { connect } from "react-redux";

import { faCaretUp } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { orderBy, uniq } from "lodash-es";
import { useIntl } from "react-intl";

import Email from "@mapmycustomers/shared/types/email/Email";
import EmailThread from "@mapmycustomers/shared/types/email/EmailThread";
import { Button, Modal } from "@mapmycustomers/ui";

import messages from "@app/component/EntityEmails/components/messages";
import getAllEmailRecipients from "@app/component/EntityEmails/util/getAllEmailRecipients";
import LongContentParagraph from "@app/component/typography/LongContentParagraph";
import { RootState } from "@app/store/rootReducer";

import { closeParentEmailThread } from "../store/actions";
import { getChildEmails, getParentEmailThread } from "../store/selectors";
import useGetEmailRecipientName from "../util/useGetEmailRecipientName";

import ChildEmailsList from "./ChildEmailsList";
import styles from "./ChildEmailsModal.module.scss";

interface ChildEmailsProps {
  emails: Email[];
  emailThread?: EmailThread;
  onCloseEmailThread: typeof closeParentEmailThread;
}

const ChildEmailsModal: React.FC<ChildEmailsProps> = ({
  emails,
  emailThread,
  onCloseEmailThread,
}) => {
  const intl = useIntl();
  const getEmailRecipientName = useGetEmailRecipientName();

  const subtitle = useMemo(() => {
    const me = intl.formatMessage(messages.meRecipient);
    return orderBy(
      uniq(
        emails
          .map((email) => [email.from[0], ...getAllEmailRecipients(email, false)])
          .flat()
          .map((recipientAddress) => getEmailRecipientName(recipientAddress))
      ),
      (name) => name === me
    ).join(", ");
  }, [intl, emails, getEmailRecipientName]);

  return (
    <Modal
      destroyOnClose
      footer={null}
      onCancel={onCloseEmailThread}
      open={!!emailThread}
      scrollable
      subtitle={
        <LongContentParagraph
          renderActionButton={(expanded, props) => (
            <Button
              {...props}
              className={styles.subtitleToggleIcon}
              icon={<FontAwesomeIcon icon={faCaretUp} rotation={expanded ? undefined : 180} />}
              iconOnly
              size="small"
              type="control"
            />
          )}
          showCollapseButtonInline
        >
          {subtitle}
        </LongContentParagraph>
      }
      title={emailThread?.subject}
      width="32.5rem"
    >
      <ChildEmailsList />
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  emails: getChildEmails(state),
  emailThread: getParentEmailThread(state),
});

const mapDispatchToProps = {
  onCloseEmailThread: closeParentEmailThread,
};

export default connect(mapStateToProps, mapDispatchToProps)(ChildEmailsModal);
